// src/screens/superadmin/users/Users.jsx
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import {
  FaSearch,
  FaUsers,
  FaCheckCircle,
  FaUserClock,
  FaBan,
  FaTimes,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaShoppingBag,
  FaDollarSign,
  FaStar,
} from "react-icons/fa";
import ActionDropdown from "../../../components/admin/dropdown/action/ActionDropdown";
import Pagination from "../../../components/general/pagination/Pagination";
import StatCard from "../../../components/admin/statcard/StatCard";
import Button from "../../../components/general/button/Button";
import "../../../assets/css/admin/customers/Customers.css";
import AdminLayout from "../../../layouts/AdminLayout";
import MyContext from "../../../context/MyContext";
import axios from "axios";
import Swal from "sweetalert2";
import { CustomersListSkeleton } from "../../../components/general/skeleton/SkeletonBlock";

// Inline SVG placeholder: no file on the server needed, so it can never 404 / loop
const PLACEHOLDER_USER =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    "<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80'>" +
      "<rect width='80' height='80' fill='#e8f5f0'/>" +
      "<circle cx='40' cy='32' r='14' fill='#0ac46c'/>" +
      "<rect x='16' y='52' width='48' height='22' rx='11' fill='#0ac46c'/>" +
      "</svg>",
  );

const CustomersList = () => {
  const navigate = useNavigate();
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [errorMessage, setErrorMessage] = useState("");
  const { baseUrl, authToken, baseUrlForFiles } = useContext(MyContext);
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(true);

  // Image helpers
  const getUserImage = (image) =>
    image ? `${baseUrlForFiles}/${image}` : PLACEHOLDER_USER;

  const handleImgError = (e) => {
    e.currentTarget.onerror = null; // stops the infinite retry loop
    e.currentTarget.src = PLACEHOLDER_USER;
  };

  const viewAllCustomers = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/admin/customer/viewAllCustomers`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.success == true) {
          setUsersList(response.data.customers);
        } else if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load customers.",
        });
      });
  };

  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "suspended", label: "Suspended" },
  ];

  // Stats calculation
  const totalUsers = usersList.length;
  const activeUsers = usersList.filter((u) => u.status === "active").length;
  const inactiveUsers = usersList.filter((u) => u.status === "inactive").length;
  const suspendedUsers = usersList.filter(
    (u) => u.status === "suspended",
  ).length;

  const statsData = [
    {
      icon: FaUsers,
      iconBg: "linear-gradient(135deg, #e3f2fd, #bbdefb)",
      iconColor: "#2196f3",
      label: "Total Users",
      value: totalUsers.toString(),
      badge: "All",
    },
    {
      icon: FaCheckCircle,
      iconBg: "linear-gradient(135deg, #e8f5f0, #d4ede3)",
      iconColor: "#0ac46c",
      label: "Active Users",
      value: activeUsers.toString(),
      badge: "Online",
    },
    {
      icon: FaUserClock,
      iconBg: "linear-gradient(135deg, #fff3e0, #ffe0b2)",
      iconColor: "#ff9800",
      label: "Inactive Users",
      value: inactiveUsers.toString(),
      badge: "Offline",
    },
    {
      icon: FaBan,
      iconBg: "linear-gradient(135deg, #ffebee, #ffcdd2)",
      iconColor: "#f44336",
      label: "Suspended Users",
      value: suspendedUsers.toString(),
      badge: "Blocked",
    },
  ];

  // View User Details
  const handleView = (user) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };

  // Enable/Disable User
  const handleToggleStatus = (userId) => {
    const formData = new FormData();
    formData.append("id", userId);
    axios
      .post(`${baseUrl}/admin/customer/updateStatus`, formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        if (response.data.success == true) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "User status updated successfully!",
          });
          viewAllCustomers();
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update user status.",
        });
      });
  };

  // Filter users (null-safe so a missing phone/email doesn't crash the page)
  const term = searchTerm.toLowerCase();
  const filteredUsers = usersList.filter((user) => {
    const matchesSearch =
      (user.name ?? "").toLowerCase().includes(term) ||
      (user.email ?? "").toLowerCase().includes(term) ||
      String(user.phone ?? "").includes(searchTerm);
    const matchesStatus =
      filterStatus === "all" || user.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getActions = (user) => {
    const actions = [
      {
        icon: "MdVisibility",
        label: "View Details",
        onClick: () => handleView(user),
      },
    ];

    // Add Enable/Disable based on status
    if (user.status === "active") {
      actions.push({
        icon: "MdBlock",
        label: "Suspend User",
        onClick: () => handleToggleStatus(user.id),
        className: "warning",
      });
    } else if (user.status === "suspended" || user.status === "inactive") {
      actions.push({
        icon: "MdCheckCircle",
        label: "Activate User",
        onClick: () => handleToggleStatus(user.id),
        className: "success",
      });
    }

    return actions;
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "active":
        return "ksa-user-badge-active";
      case "inactive":
        return "ksa-user-badge-inactive";
      case "suspended":
        return "ksa-user-badge-suspended";
      default:
        return "ksa-user-badge-default";
    }
  };

  const getStatusLabel = (status) => {
    return status;
  };

  useEffect(() => {
    if (!authToken) {
      setLoading(false);
      navigate("/admin/login");
      return;
    }
    viewAllCustomers();
  }, [authToken]);

  if (loading) {
    return (
      <AdminLayout>
        <CustomersListSkeleton />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        {/* Page Header */}
        <div className="ksa-user-page-header">
          <div>
            <h1 className="ksa-layout-page-title">Customer Management</h1>
            <p className="ksa-layout-page-subtitle">
              Manage customer accounts and monitor customer activity.
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <Row className="g-4 mb-4 ksa-user-stats">
          {statsData.map((stat, idx) => (
            <Col key={idx} xs={12} sm={6} xl={3}>
              <StatCard {...stat} />
            </Col>
          ))}
        </Row>

        {/* Main Table Card */}
        <div className="ksa-card">
          {/* Filters and Search */}
          <div className="ksa-user-table-header">
            <div className="ksa-user-filters">
              <select
                className="ksa-user-filter-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="ksa-user-search">
              <FaSearch size={16} />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Table */}
          <div className="ksa-user-table-wrapper">
            <table className="ksa-user-table">
              <thead>
                <tr>
                  <th>IMAGE</th>
                  <th>USER NAME</th>
                  <th>EMAIL</th>
                  <th>PHONE</th>
                  <th>CITY</th>
                  <th>TOTAL ORDERS</th>
                  <th>TOTAL SPENT</th>
                  <th>STATUS</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers
                  .slice(
                    (currentPage - 1) * itemsPerPage,
                    currentPage * itemsPerPage,
                  )
                  .map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div className="ksa-user-image">
                          <img
                            src={getUserImage(user.image)}
                            alt={user.name}
                            onError={handleImgError}
                          />
                        </div>
                      </td>
                      <td className="ksa-user-name">{user.name}</td>
                      <td className="ksa-user-email">{user.email}</td>
                      <td className="ksa-user-phone">{user.phone}</td>
                      <td className="ksa-user-city">{user.city}</td>
                      <td className="ksa-user-orders">{user.totalOrders}</td>
                      <td className="ksa-user-spent">
                        {(user.totalSpent / 1000).toFixed(1)}K
                      </td>
                      <td>
                        <span
                          className={`ksa-user-status-badge ${getStatusBadgeClass(user.status)}`}
                        >
                          {getStatusLabel(user.status)}
                        </span>
                      </td>
                      <td>
                        <ActionDropdown actions={getActions(user)} />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <Pagination
            totalItems={filteredUsers.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        </div>

        {/* View User Details Modal */}
        {showViewModal && selectedUser && (
          <div
            className="ksa-modal-overlay"
            onClick={() => setShowViewModal(false)}
          >
            <div
              className="ksa-modal ksa-modal-lg ksa-user-view-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="ksa-user-modal-header">
                <h3 className="ksa-user-modal-title">
                  User Profile: {selectedUser.name}
                </h3>
                <button
                  className="ksa-modal-close"
                  onClick={() => setShowViewModal(false)}
                >
                  <FaTimes size={18} />
                </button>
              </div>

              <div className="ksa-user-modal-body">
                <Row className="g-4">
                  {/* Left Column - Profile Info */}
                  <Col xs={12} md={4}>
                    <div className="ksa-user-profile-card">
                      <div className="ksa-user-profile-image">
                        <img
                          src={getUserImage(selectedUser.image)}
                          alt={selectedUser.name}
                          onError={handleImgError}
                        />
                      </div>
                      <h4 className="ksa-user-profile-name">
                        {selectedUser.name}
                      </h4>
                      <span
                        className={`ksa-user-status-badge ${getStatusBadgeClass(selectedUser.status)}`}
                      >
                        {getStatusLabel(selectedUser.status)}
                      </span>

                      <div className="ksa-user-profile-info">
                        <div className="ksa-user-info-item">
                          <FaEnvelope />
                          <span>{selectedUser.email}</span>
                        </div>
                        <div className="ksa-user-info-item">
                          <FaPhone />
                          <span>{selectedUser.phone}</span>
                        </div>
                        <div className="ksa-user-info-item">
                          <FaMapMarkerAlt />
                          <span>{selectedUser.address}</span>
                        </div>
                        <div className="ksa-user-info-item">
                          <FaCalendarAlt />
                          <span>Joined: {selectedUser.joinedDate}</span>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="ksa-user-profile-stats">
                        <div className="ksa-user-stat-item">
                          <FaShoppingBag className="ksa-user-stat-icon" />
                          <div>
                            <p className="ksa-user-stat-value">
                              {selectedUser.totalOrders}
                            </p>
                            <p className="ksa-user-stat-label">Total Orders</p>
                          </div>
                        </div>
                        <div className="ksa-user-stat-item">
                          <FaDollarSign className="ksa-user-stat-icon" />
                          <div>
                            <p className="ksa-user-stat-value">
                              ₹{(selectedUser.totalSpent / 1000).toFixed(1)}K
                            </p>
                            <p className="ksa-user-stat-label">Total Spent</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Col>

                  {/* Right Column - Details */}
                  <Col xs={12} md={8}>
                    {/* Account Details */}
                    <div className="ksa-user-details-section">
                      <h5 className="ksa-user-section-title">
                        Account Details
                      </h5>
                      <div className="ksa-user-details-grid">
                        <div className="ksa-user-detail-item">
                          <label>Full Name</label>
                          <p>{selectedUser.name}</p>
                        </div>
                        <div className="ksa-user-detail-item">
                          <label>Email Address</label>
                          <p>{selectedUser.email}</p>
                        </div>
                        <div className="ksa-user-detail-item">
                          <label>Phone Number</label>
                          <p>{selectedUser.phone}</p>
                        </div>
                        <div className="ksa-user-detail-item">
                          <label>City</label>
                          <p>{selectedUser.city}</p>
                        </div>
                        <div className="ksa-user-detail-item ksa-user-detail-full">
                          <label>Address</label>
                          <p>{selectedUser.address}</p>
                        </div>
                        <div className="ksa-user-detail-item">
                          <label>Joined Date</label>
                          <p>{selectedUser.joinedDate}</p>
                        </div>
                        <div className="ksa-user-detail-item">
                          <label>Last Active</label>
                          <p>{selectedUser.lastActive}</p>
                        </div>
                        <div className="ksa-user-detail-item">
                          <label>Account Status</label>
                          <span
                            className={`ksa-user-status-badge ${getStatusBadgeClass(selectedUser.status)}`}
                          >
                            {getStatusLabel(selectedUser.status)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Activity Summary */}
                    <div className="ksa-user-details-section">
                      <h5 className="ksa-user-section-title">
                        Activity Summary
                      </h5>
                      <div className="ksa-user-activity-cards">
                        <div className="ksa-user-activity-card">
                          <div
                            className="ksa-user-activity-icon"
                            style={{ background: "#e8f5f0", color: "#0ac46c" }}
                          >
                            <FaShoppingBag size={24} />
                          </div>
                          <div className="ksa-user-activity-details">
                            <h6>{selectedUser.totalOrders}</h6>
                            <p>Total Orders</p>
                          </div>
                        </div>
                        <div className="ksa-user-activity-card">
                          <div
                            className="ksa-user-activity-icon"
                            style={{ background: "#e3f2fd", color: "#2196f3" }}
                          >
                            <FaDollarSign size={24} />
                          </div>
                          <div className="ksa-user-activity-details">
                            <h6>{selectedUser.totalSpent}</h6>
                            <p>Total Spent</p>
                          </div>
                        </div>
                        <div className="ksa-user-activity-card">
                          <div
                            className="ksa-user-activity-icon"
                            style={{ background: "#fff3e0", color: "#ff9800" }}
                          >
                            <FaStar size={24} />
                          </div>
                          <div className="ksa-user-activity-details">
                            <h6>
                              {selectedUser.totalOrders > 0
                                ? (
                                    selectedUser.totalSpent /
                                    selectedUser.totalOrders
                                  ).toFixed(0)
                                : "0"}
                            </h6>
                            <p>Avg Order Value</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Additional Info */}
                    <div className="ksa-user-details-section">
                      <h5 className="ksa-user-section-title">
                        Additional Information
                      </h5>
                      <div className="ksa-user-info-box">
                        <p>
                          <strong>Customer Type:</strong>{" "}
                          {selectedUser.totalOrders > 20
                            ? "Premium Customer"
                            : selectedUser.totalOrders > 5
                              ? "Regular Customer"
                              : "New Customer"}
                        </p>
                        <p>
                          <strong>Registration Date:</strong>{" "}
                          {selectedUser.joinedDate}
                        </p>
                        <p>
                          <strong>Last Login:</strong> {selectedUser.lastActive}
                        </p>
                        <p>
                          <strong>Preferred City:</strong> {selectedUser.city}
                        </p>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>

              <div className="ksa-user-modal-footer">
                <Button
                  variant="secondary"
                  onClick={() => setShowViewModal(false)}
                >
                  Close
                </Button>
                {selectedUser.status === "active" && (
                  <Button
                    variant="warning"
                    onClick={() => {
                      setShowViewModal(false);
                      handleToggleStatus(selectedUser.id);
                    }}
                  >
                    Suspend User
                  </Button>
                )}
                {(selectedUser.status === "suspended" ||
                  selectedUser.status === "inactive") && (
                  <Button
                    variant="primary"
                    onClick={() => {
                      setShowViewModal(false);
                      handleToggleStatus(selectedUser.id);
                    }}
                  >
                    Activate User
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default CustomersList;
